"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Section } from "@/components/layout/Section"
import Link from "next/link"
import Image from "next/image"
import { ArrowRight } from "lucide-react"
import { getBlogs } from "@/lib/cms/queries"
import type { Blog } from "@/types/database"
import { getTranslations } from "@/lib/translations"
import { isValidLocale, type Locale } from "@/lib/i18n"


function BlogPostCard({ post, index, locale, t }: { post: Blog, index: number, locale: Locale, t: any }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      suppressHydrationWarning
    >
      {/* The whole card is the link (image, title, excerpt and "read more") */}
      <Link
        href={locale === 'tr' ? `/blog/${post.slug || post.id}` : `/${locale}/blog/${post.slug || post.id}`}
        className="block h-full rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#034AA6]"
      >
      <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 border-0 bg-white group cursor-pointer h-full flex flex-col">
        <div className="relative h-48 sm:h-56 md:h-64 w-full overflow-hidden" suppressHydrationWarning>
          {post.image ? (
            <Image
              src={post.image}
              alt={post.title}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-500"
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-slate-200">
              <span className="text-slate-400 text-sm">{t.blog.noImage}</span>
            </div>
          )}
        </div>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-xl mb-2 line-clamp-2">{post.title}</CardTitle>
          <CardDescription className="text-sm sm:text-base text-slate-600 line-clamp-2">{post.excerpt}</CardDescription>
        </CardHeader>
        <CardContent className="mt-auto p-4 sm:p-6 pt-0">
          <span className="inline-flex w-full items-center justify-start rounded-md px-4 py-2 text-sm font-medium group-hover:bg-slate-100 transition-colors">
            {t.blog.readMore}
            <ArrowRight className="ml-2 h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </CardContent>
      </Card>
      </Link>
    </motion.div>
  )
}

type BlogPreviewProps = {
  locale?: string
}

export function BlogPreview({ locale }: BlogPreviewProps) {
  const [blogPosts, setBlogPosts] = useState<Blog[]>([])
  const [loading, setLoading] = useState(true)
  
  // Get current locale and translations
  const currentLocale: Locale = (locale && isValidLocale(locale)) ? locale : 'tr'
  const t = getTranslations(currentLocale)

  useEffect(() => {
    loadBlogs()
  }, [currentLocale])

  const loadBlogs = async () => {
    try {
      // Sadece Supabase'den published blogları getir - locale filtresi ile
      const blogs = await getBlogs('published', currentLocale, 3)
      // En son 3 blogu göster
      const latestBlogs = blogs.slice(0, 3)
      setBlogPosts(latestBlogs)
    } catch (error) {
      console.error("Error loading blogs:", error)
      // Hata durumunda boş array - sadece veritabanından çek
      setBlogPosts([])
    } finally {
      setLoading(false)
    }
  }

  return (
    <Section className="bg-white pt-16 sm:pt-20 md:pt-24 lg:pt-32 pb-8 sm:pb-12 md:pb-16 lg:pb-20">
      <div className="mb-12 sm:mb-16" suppressHydrationWarning>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-4 sm:mb-6 font-mono"
        >
          {t.blog.title}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-base sm:text-lg md:text-xl text-slate-600 max-w-3xl"
        >
          {t.blog.description}
        </motion.p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">{t.blog.loading}</div>
      ) : blogPosts.length === 0 ? (
        <div className="text-center py-12 text-slate-500">{t.blog.empty}</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-3 mb-8 sm:mb-12" suppressHydrationWarning>
          {blogPosts.map((post, index) => (
            <BlogPostCard key={post.id} post={post} index={index} locale={currentLocale} t={t} />
          ))}
        </div>
      )}

      {/* View All Button - Bottom */}
      <div className="relative z-10 flex justify-end items-center gap-2 text-slate-400 mt-8 sm:mt-12" suppressHydrationWarning>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          suppressHydrationWarning
        >
          <Link href={currentLocale === 'tr' ? '/blog' : `/${currentLocale}/blog`} className="flex items-center gap-2 group hover:text-slate-600 transition-colors">
            <span className="text-xs sm:text-sm font-medium">{t.blog.viewAll}</span>
            <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </Section>
  )
}

